/**
 * Refresh PROATIVO do access token — mantém o token "quente" renovando-o ANTES
 * de vencer, num timer, em vez de só reativamente.
 *
 * Contexto: o realm `candidate` usa `accessTokenLifespan=300s (5 min)`, e o
 * `KeycloakGate` só refresca sob demanda — `updateToken(60)` por request
 * (`setAccessTokenProvider`) + `updateToken(-1)` na recuperação de 401
 * (`setForceTokenRefresh`, EU-665). Sem um timer que antecipe a expiração, a
 * cada ~5 min o token vence e uma leva de requests concorrentes (boot da página,
 * refetch-on-focus, várias queries TanStack em paralelo) racea no boundary →
 * rajada de 401 em ~1ms (recuperada ~98-100% pelo EU-665, mas ~56k/dia de ruído
 * nos logs, e um custo de latência/UX no instante da expiração).
 *
 * Este timer refresca a cada `intervalMs` garantindo pelo menos `minValidityS`
 * de validade — com `intervalMs < minValidityS`, o token nunca chega ao boundary
 * (a rajada de 401 desaparece). NÃO mexe no TTL nem no realm (segurança intacta);
 * é forward-only e os caminhos reativos seguem como fallback.
 */

/** Checa/renova a cada 60 s. Menor que `MIN_VALIDITY` ⇒ token sempre quente. */
export const TOKEN_REFRESH_INTERVAL_MS = 60_000;

/**
 * Renova se faltar menos que 150 s de validade (metade do TTL de 300 s). Com o
 * intervalo de 60 s, o token é renovado com folga de ≥ 90 s sobre latência +
 * skew de relógio — nenhuma request sai com o bearer no fio da navalha.
 */
export const TOKEN_REFRESH_MIN_VALIDITY_S = 150;

/** Subconjunto do `keycloak-js` que este timer usa — mantém o módulo testável. */
interface RefreshableKeycloak {
  updateToken(minValidity: number): Promise<boolean>;
}

/**
 * Inicia o refresh proativo. Devolve a função de limpeza (para o timer) — o
 * chamador (`KeycloakGate`) deve invocá-la no cleanup do effect. Uma falha do
 * `updateToken` é apenas logada (o EU-665 ainda cobre o 401 seguinte); o timer
 * segue vivo para a próxima tentativa.
 */
export function startProactiveTokenRefresh(
  keycloak: RefreshableKeycloak,
  options: { intervalMs?: number; minValidityS?: number } = {}
): () => void {
  const intervalMs = options.intervalMs ?? TOKEN_REFRESH_INTERVAL_MS;
  const minValidityS = options.minValidityS ?? TOKEN_REFRESH_MIN_VALIDITY_S;

  const timer = setInterval(() => {
    void keycloak.updateToken(minValidityS).catch((err) => {
      console.error('[KeycloakGate] refresh proativo de token falhou (não-fatal)', err);
    });
  }, intervalMs);

  return () => clearInterval(timer);
}
